import { useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Simple lightbox component. Renders a grid of thumbnails; clicking opens
 * a fullscreen viewer with keyboard + button navigation.
 */
export default function PhotoGallery({ photos, testIdPrefix = "gallery-photo" }) {
  const [openIdx, setOpenIdx] = useState(-1);
  const isOpen = openIdx >= 0;

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpenIdx(-1);
      if (e.key === "ArrowRight") setOpenIdx((i) => (i + 1) % photos.length);
      if (e.key === "ArrowLeft")
        setOpenIdx((i) => (i - 1 + photos.length) % photos.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, photos.length]);

  if (!photos?.length) return null;

  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3">
        {photos.map((src, i) => (
          <button
            key={src}
            type="button"
            data-testid={`${testIdPrefix}-${i}`}
            onClick={() => setOpenIdx(i)}
            className={`group relative overflow-hidden bg-[#f2f2f2] rounded-lg ${
              i === 0
                ? "col-span-2 row-span-2 aspect-square"
                : "aspect-square"
            }`}
          >
            <img
              src={src}
              alt=""
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          </button>
        ))}
      </div>

      {isOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex items-center justify-center p-3 sm:p-6"
          onClick={() => setOpenIdx(-1)}
          data-testid="photo-lightbox"
        >
          <button
            data-testid="photo-lightbox-close"
            onClick={(e) => {
              e.stopPropagation();
              setOpenIdx(-1);
            }}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center backdrop-blur-md"
            aria-label="Close"
          >
            <X size={18} />
          </button>
          <button
            data-testid="photo-lightbox-prev"
            onClick={(e) => {
              e.stopPropagation();
              setOpenIdx((i) => (i - 1 + photos.length) % photos.length);
            }}
            className="absolute left-3 sm:left-6 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center backdrop-blur-md"
            aria-label="Previous"
          >
            <ChevronLeft size={22} />
          </button>
          <button
            data-testid="photo-lightbox-next"
            onClick={(e) => {
              e.stopPropagation();
              setOpenIdx((i) => (i + 1) % photos.length);
            }}
            className="absolute right-3 sm:right-6 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center backdrop-blur-md"
            aria-label="Next"
          >
            <ChevronRight size={22} />
          </button>
          <img
            src={photos[openIdx]}
            alt=""
            className="max-h-[90vh] max-w-[95vw] w-auto h-auto object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 text-white/80 text-xs tracking-widest uppercase">
            {openIdx + 1} / {photos.length}
          </div>
        </div>
      )}
    </>
  );
}
